import React from 'react';
import FinalDecisionPanel from '../../../components/FinalDecisionPanel';
import { LANGUAGE_OPTIONS } from '../../../i18n';
import LockOverlay from './LockOverlay';

/**
 * Right-side settings sidebar shown when the user opens "Settings" on the
 * Board Editor. Owns no state — every form field reads from the parent's
 * `board` snapshot and writes back through `handleUpdateSettings`.
 *
 * The "Selected image" panel at the bottom is only shown when an image is
 * selected via footer-click.
 */
const BoardSettingsPanel = ({
  t,
  board,
  setBoard,
  showSettings,
  setShowSettings,
  workspaceLang,
  featureAccess,
  sectionLabels,
  selectedImage,
  setSelectedImage,
  setShowLabelsModal,
  setShowDeleteBoardModal,
  handleUpdateSettings,
  handleUpdateImage,
  handleDeleteImage,
  handleArchiveBoard,
  handleRestoreBoard,
  showToast,
  boardsAPI,
  // Exports — moved here from the top header to reduce nav clutter.
  exporting,
  exportingZip,
  boardArchiveStatus,
  setShowExportModal,
  onExportZip,
}) => {
  if (!showSettings) return null;

  // Inspiration boards present a curated concept — no reactions, no
  // conversations, no decisions. The collab-oriented panels below are
  // suppressed for inspiration boards; conversion to a Client Board
  // re-enables everything (no data is destroyed).
  const isInspiration = (board && board.board_type === 'inspiration');

  // Convert an inspiration board into a fully-collaborative client
  // board. Idempotent in spirit — if the user double-clicks the API
  // call simply confirms the new state. Confirmation prompt mirrors
  // the wording from the product spec.
  const handleConvertToClient = async () => {
    if (!window.confirm(t.bs_convert_confirm || 'Convert this Inspiration Board into a Client Board? This will enable collaboration, comments, uploads, documents, and approval workflows.')) return;
    try {
      const updated = await handleUpdateSettings({ board_type: 'client' });
      showToast && showToast(t.bs_convert_success || 'Board converted to a Client Board.');
      // setBoard fallback if handleUpdateSettings doesn't return the new doc
      if (!updated) {
        setBoard && setBoard((prev) => (prev ? { ...prev, board_type: 'client' } : prev));
      }
    } catch (err) {
      showToast && showToast(err?.response?.data?.detail || 'Could not convert board');
    }
  };

  return (
    <div className="editor-sidebar" data-testid="board-settings-panel">
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 16,
        marginBottom: 24,
      }}>
        <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', margin: 0, lineHeight: 1.2 }}>
          {t.board_settings}
        </h3>
        <button
          type="button"
          data-testid="close-board-settings-btn"
          onClick={() => setShowSettings(false)}
          aria-label={t.close || 'Close'}
          title={t.close || 'Close'}
          style={{
            width: 32,
            height: 32,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: '1px solid var(--kahla-border)',
            color: 'var(--kahla-charcoal)',
            fontSize: 16,
            lineHeight: 1,
            cursor: 'pointer',
            flexShrink: 0,
            transition: 'background 160ms ease, border-color 160ms ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--kahla-cream-light)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          ✕
        </button>
      </div>

      <div className="form-group">
        <label>{t.board_name}</label>
        <input
          type="text"
          defaultValue={board.name}
          onBlur={(e) => handleUpdateSettings({ name: e.target.value })}
        />
      </div>

      <div className="form-group">
        <label>{t.client_name}</label>
        <input
          type="text"
          defaultValue={board.client_name}
          onBlur={(e) => handleUpdateSettings({ client_name: e.target.value })}
        />
      </div>

      <div className="form-group">
        <label>{t.client_email_label}</label>
        <input
          data-testid="client-email-input"
          type="email"
          defaultValue={board.client_email || ''}
          placeholder="client@example.com"
          onBlur={(e) => handleUpdateSettings({ client_email: e.target.value })}
        />
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
          marginTop: '2px',
        }}>
          <span style={{ fontSize: '10px', color: 'var(--kahla-text-light)' }}>
            {t.used_for_client_notifications}
          </span>
          {featureAccess.allow_client_notifications && (
            <button
              type="button"
              data-testid="send-test-email-btn"
              onClick={async () => {
                try {
                  const res = await boardsAPI.notifyTest(board.id);
                  showToast(`Test email sent to ${res.data?.email || 'you'}`);
                } catch (err) {
                  showToast(err.response?.data?.detail || 'Could not send test email');
                }
              }}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                fontSize: '10px',
                letterSpacing: '0.04em',
                color: 'var(--kahla-taupe)',
                cursor: 'pointer',
                fontFamily: 'inherit',
                textDecoration: 'underline',
                textDecorationColor: 'rgba(166,152,136,0.4)',
                textUnderlineOffset: '3px',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--kahla-charcoal)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--kahla-taupe)'; }}
              title="Send the notification email to yourself as a preview"
            >
              {t.send_test_to_me}
            </button>
          )}
        </div>
      </div>

      <div className="form-group">
        <label>{t.client_details}</label>
        <textarea
          defaultValue={board.client_details || ''}
          onBlur={(e) => handleUpdateSettings({ client_details: e.target.value })}
          placeholder={t.client_details_placeholder}
          data-testid="board-client-details"
          style={{ width: '100%', minHeight: '72px', resize: 'vertical', fontSize: '13px', padding: '8px 10px', border: '1px solid var(--kahla-border)', fontFamily: 'inherit', boxSizing: 'border-box' }}
        />
      </div>

      <div className="form-group">
        <label>{t.session_date}</label>
        <input
          type="date"
          defaultValue={board.session_date?.split('T')[0] || ''}
          onChange={(e) => handleUpdateSettings({ session_date: e.target.value ? new Date(e.target.value).toISOString() : null })}
        />
      </div>

      <div className="form-group">
        <label>{t.link_expiration}</label>
        <select
          defaultValue={board.expiration_days}
          onChange={(e) => handleUpdateSettings({ expiration_days: parseInt(e.target.value) })}
        >
          <option value={7}>7 {t.days}</option>
          <option value={14}>14 {t.days}</option>
          <option value={30}>30 {t.days}</option>
          <option value={60}>60 {t.days}</option>
        </select>
      </div>

      <div className="form-group">
        <label>{t.auto_lock_label}</label>
        <select
          defaultValue={board.lock_days_before}
          onChange={(e) => handleUpdateSettings({ lock_days_before: parseInt(e.target.value) })}
        >
          <option value={3}>3 {t.days}</option>
          <option value={5}>5 {t.days}</option>
          <option value={7}>7 {t.days}</option>
          <option value={30}>30 {t.days}</option>
          <option value={60}>60 {t.days}</option>
        </select>
      </div>

      {/* ── Board Type panel ──────────────────────────────────────────
          Surfaces the board's current mode and offers a one-click
          conversion from Inspiration → Client. Always rendered so the
          owner can see what kind of board they're editing. */}
      <div className="settings-panel" data-testid="board-type-panel">
        <p className="settings-panel-title">{t.bs_board_type_panel_title || 'Board Type'}</p>
        <p style={{
          fontFamily: 'Cormorant Garamond, Playfair Display, serif',
          fontStyle: 'italic',
          fontSize: '13px',
          lineHeight: 1.55,
          color: 'var(--kahla-text-light)',
          margin: '-2px 0 14px',
        }}>
          {isInspiration
            ? (t.bs_board_type_inspiration_summary || 'A curated, presentation-only board — no collaboration features.')
            : (t.bs_board_type_client_summary || 'Full collaborative workspace with feedback, decisions, and documents.')}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span
            data-testid="board-type-badge"
            style={{
              display: 'inline-block',
              padding: '4px 12px',
              fontSize: 10,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: isInspiration ? '#7a5b2e' : 'var(--kahla-charcoal, #2a2620)',
              background: isInspiration ? '#f6efe0' : 'transparent',
              border: '1px solid ' + (isInspiration ? '#d8c597' : 'var(--kahla-border)'),
            }}
          >
            {isInspiration ? (t.cbm_board_type_inspiration || 'Inspiration Board') : (t.cbm_board_type_client || 'Client Board')}
          </span>
          {isInspiration && (
            <button
              type="button"
              onClick={handleConvertToClient}
              data-testid="convert-to-client-btn"
              style={{
                background: 'none',
                border: '1px solid var(--kahla-border)',
                padding: '8px 16px',
                fontSize: 11,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: 'var(--kahla-text)',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >{t.bs_convert_to_client_btn || 'Convert to Client Board'}</button>
          )}
        </div>
        {isInspiration && (
          <p
            data-testid="board-type-inspiration-hint"
            style={{
              fontFamily: 'Cormorant Garamond, Playfair Display, serif',
              fontStyle: 'italic',
              fontSize: 12,
              lineHeight: 1.55,
              color: 'var(--kahla-text-light)',
              margin: '14px 0 0',
            }}
          >
            {t.bs_inspiration_features_hidden || 'These features are part of the Client Board experience. Convert this board to enable them.'}
          </p>
        )}
      </div>

      {!isInspiration && (
      <FinalDecisionPanel
        board={board}
        t={t}
        lang={(workspaceLang || 'en').toLowerCase()}
        featureAccess={featureAccess}
        LockOverlay={!featureAccess.allow_final_decision_mode ? LockOverlay : null}
        onChange={(patch) => setBoard((prev) => (prev ? { ...prev, ...patch } : prev))}
      />
      )}

      {/* Shared Decision Mode — always rendered; locked when plan disallows */}
      {!isInspiration && (
      <div className="settings-panel">
        <p className="settings-panel-title">{t.shared_decision_mode}</p>
        <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)', marginBottom: '12px', lineHeight: '1.6' }}>
          {t.shared_decision_desc}
        </p>
        <label style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          cursor: featureAccess.allow_shared_decision ? 'pointer' : 'not-allowed',
          fontSize: '13px', padding: '10px 0',
          opacity: featureAccess.allow_shared_decision ? 1 : 0.55,
        }}>
          <input
            type="checkbox"
            checked={featureAccess.allow_shared_decision && (board.shared_decision_mode || false)}
            onChange={(e) => handleUpdateSettings({ shared_decision_mode: e.target.checked })}
            disabled={!featureAccess.allow_shared_decision}
            style={{ width: '16px', height: '16px', accentColor: '#8ea0b0' }}
            data-testid="shared-decision-toggle"
          />
          <span style={{ color: (featureAccess.allow_shared_decision && board.shared_decision_mode) ? '#8ea0b0' : 'var(--kahla-text)' }}>
            {featureAccess.allow_shared_decision
              ? (board.shared_decision_mode ? t.shared_mode_enabled : t.enable_shared_decisions)
              : `🔒 ${t.shared_decision_mode}`}
          </span>
        </label>
        {!featureAccess.allow_shared_decision && <LockOverlay />}
        {featureAccess.allow_shared_decision && board.shared_decision_mode && (
          <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
            <input
              data-testid="participant-name-0"
              type="text"
              placeholder={t.person_1_name}
              defaultValue={(board.shared_decision_participants || [])[0] || ''}
              onBlur={(e) => {
                const parts = [...(board.shared_decision_participants || ['', ''])];
                parts[0] = e.target.value;
                handleUpdateSettings({ shared_decision_participants: parts });
              }}
              style={{ flex: 1, padding: '8px 10px', fontSize: '13px', border: '1px solid var(--kahla-border)', fontFamily: 'inherit', background: 'var(--kahla-white)' }}
            />
            <input
              data-testid="participant-name-1"
              type="text"
              placeholder={t.person_2_name}
              defaultValue={(board.shared_decision_participants || [])[1] || ''}
              onBlur={(e) => {
                const parts = [...(board.shared_decision_participants || ['', ''])];
                parts[1] = e.target.value;
                handleUpdateSettings({ shared_decision_participants: parts });
              }}
              style={{ flex: 1, padding: '8px 10px', fontSize: '13px', border: '1px solid var(--kahla-border)', fontFamily: 'inherit', background: 'var(--kahla-white)' }}
            />
          </div>
        )}
      </div>
      )}

      <div className="settings-panel">
        <p className="settings-panel-title">{t.client_language}</p>
        <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)', marginBottom: '12px', lineHeight: '1.6' }}>
          {t.client_language_desc}
        </p>
        <select
          value={board.client_language || 'en'}
          onChange={(e) => handleUpdateSettings({ client_language: e.target.value })}
          data-testid="client-language-select"
          style={{
            width: '100%',
            padding: '10px 12px',
            fontSize: '13px',
            border: '1px solid var(--kahla-border)',
            backgroundColor: 'var(--kahla-white)',
            color: 'var(--kahla-text)',
            cursor: 'pointer',
          }}
        >
          {LANGUAGE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {!isInspiration && (
      <div className="settings-panel" data-testid="client-communication-panel">
        <p className="settings-panel-title">{t.client_communication}</p>
        <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)', marginBottom: '12px', lineHeight: '1.6' }}>
          {t.client_communication_desc}
        </p>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          cursor: featureAccess.allow_notes ? 'pointer' : 'not-allowed',
          fontSize: '13px',
          padding: '10px 0',
          opacity: featureAccess.allow_notes ? 1 : 0.55,
        }}>
          <input
            type="checkbox"
            checked={featureAccess.allow_notes && (board.allow_notes !== false)}
            onChange={(e) => handleUpdateSettings({ allow_notes: e.target.checked })}
            disabled={!featureAccess.allow_notes}
            style={{ width: '16px', height: '16px', accentColor: '#C9A96E' }}
            data-testid="allow-notes-toggle"
          />
          <span style={{ color: (featureAccess.allow_notes && board.allow_notes !== false) ? 'var(--kahla-text)' : 'var(--kahla-text-light)' }}>
            {featureAccess.allow_notes ? (board.allow_notes !== false ? t.notes_enabled : t.notes_hidden) : `🔒 ${t.client_notes_locked}`}
          </span>
        </label>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          cursor: featureAccess.allow_client_uploads ? 'pointer' : 'not-allowed',
          fontSize: '13px',
          padding: '10px 0',
          opacity: featureAccess.allow_client_uploads ? 1 : 0.55,
        }}>
          <input
            type="checkbox"
            checked={featureAccess.allow_client_uploads && (board.allow_client_uploads !== false)}
            onChange={(e) => handleUpdateSettings({ allow_client_uploads: e.target.checked })}
            disabled={!featureAccess.allow_client_uploads}
            style={{ width: '16px', height: '16px', accentColor: '#C9A96E' }}
            data-testid="allow-client-uploads-toggle"
          />
          <span style={{ color: (featureAccess.allow_client_uploads && board.allow_client_uploads !== false) ? 'var(--kahla-text)' : 'var(--kahla-text-light)' }}>
            {featureAccess.allow_client_uploads ? (board.allow_client_uploads !== false ? t.client_uploads_enabled : t.client_uploads_hidden) : `🔒 ${t.client_uploads_locked}`}
          </span>
        </label>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          cursor: featureAccess.allow_documents ? 'pointer' : 'not-allowed',
          fontSize: '13px',
          padding: '10px 0',
          opacity: featureAccess.allow_documents ? 1 : 0.55,
        }}>
          <input
            type="checkbox"
            checked={featureAccess.allow_documents && (board.allow_documents !== false)}
            onChange={(e) => handleUpdateSettings({ allow_documents: e.target.checked })}
            disabled={!featureAccess.allow_documents}
            style={{ width: '16px', height: '16px', accentColor: '#C9A96E' }}
            data-testid="allow-documents-toggle"
          />
          <span style={{ color: (featureAccess.allow_documents && board.allow_documents !== false) ? 'var(--kahla-text)' : 'var(--kahla-text-light)' }}>
            {featureAccess.allow_documents ? (board.allow_documents !== false ? t.documents_enabled : t.documents_hidden) : `🔒 ${t.documents_locked}`}
          </span>
        </label>
        {(!featureAccess.allow_notes || !featureAccess.allow_client_uploads || !featureAccess.allow_documents) && <LockOverlay />}
      </div>
      )}


      {/* Internal view — studio-side display preferences. Never visible to the client. */}
      <div className="settings-panel" data-testid="internal-view-panel">
        <p className="settings-panel-title">{t.internal_view_title}</p>
        <p
          style={{
            fontSize: '12px',
            color: 'var(--kahla-text-light)',
            marginBottom: '14px',
            lineHeight: 1.6,
            fontFamily: '"Cormorant Garamond", serif',
            fontStyle: 'italic',
          }}
        >
          {t.internal_view_desc}
        </p>
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            cursor: 'pointer',
            fontSize: '13px',
            padding: '4px 0',
          }}
        >
          <input
            type="checkbox"
            checked={board.show_filenames === true}
            onChange={(e) => handleUpdateSettings({ show_filenames: e.target.checked })}
            style={{ width: '16px', height: '16px', accentColor: '#C9A96E', marginTop: '2px' }}
            data-testid="show-filenames-toggle"
          />
          <span style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <span style={{ color: 'var(--kahla-text)' }}>{t.show_filenames_label}</span>
            <span
              style={{
                fontSize: '11px',
                color: 'var(--kahla-text-light)',
                fontFamily: '"Cormorant Garamond", serif',
                fontStyle: 'italic',
                lineHeight: 1.5,
              }}
            >
              {t.show_filenames_hint}
            </span>
          </span>
        </label>
      </div>

      {/* ────────────────────────────────────────────────────────────
          Exports — the deliverable moment.

          This is the magic moment for Maestra: the finished moodboard
          becomes a PDF to share with the client, or a ZIP to archive
          for the studio. The panel is rendered with editorial weight —
          a quiet Playfair italic subtitle, two card-style buttons with
          tiny serif subtitles, a hairline gold accent on the primary
          (PDF) action, and the quota line moved to a calm footer.
          ──────────────────────────────────────────────────────── */}
      <div className="settings-panel" data-testid="board-exports-panel">
        <p className="settings-panel-title">{t.exports}</p>
        <p style={{
          fontFamily: 'Cormorant Garamond, Playfair Display, serif',
          fontStyle: 'italic',
          fontSize: '13px',
          lineHeight: 1.55,
          color: 'var(--kahla-text-light)',
          margin: '-2px 0 16px',
        }}>
          {t.exports_panel_subtitle}
        </p>

        {(() => {
          const archive = boardArchiveStatus;
          const pdfDisabled = exporting || (archive?.active && archive?.pdf?.remaining === 0);
          const zipDisabled = exportingZip || (archive?.active && archive?.zip?.remaining === 0);

          // Card-style export action — primary or secondary variant.
          //   primary  → faint gold inset border (the deliverable)
          //   secondary→ plain hairline border (the archive)
          const ExportAction = ({
            variant, label, subtitle, onClick, disabled,
            loading, loadingLabel, testId, IconSvg,
          }) => (
            <button
              type="button"
              onClick={onClick}
              disabled={disabled}
              data-testid={testId}
              style={{
                display: 'flex', alignItems: 'center', gap: '14px',
                width: '100%', textAlign: 'left',
                padding: '14px 16px',
                background: variant === 'primary' ? 'var(--kahla-cream, #f6f1e7)' : 'transparent',
                border: variant === 'primary'
                  ? '1px solid #d8c597'
                  : '1px solid var(--kahla-border)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.55 : 1,
                fontFamily: 'inherit',
                transition: 'background 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
                position: 'relative',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                if (disabled) return;
                e.currentTarget.style.transform = 'translateY(-1px)';
                if (variant !== 'primary') e.currentTarget.style.borderColor = '#b8a37a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                if (variant !== 'primary') e.currentTarget.style.borderColor = 'var(--kahla-border)';
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 24, height: 24, flexShrink: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  color: variant === 'primary' ? '#8b6a2e' : 'var(--kahla-text-light)',
                }}
              >
                <IconSvg />
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 }}>
                <span style={{
                  fontFamily: 'Playfair Display, serif',
                  fontSize: '15px',
                  color: 'var(--kahla-charcoal, #2a2620)',
                  letterSpacing: '-0.005em',
                  lineHeight: 1.2,
                }}>
                  {loading ? loadingLabel : label}
                </span>
                <span style={{
                  fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                  fontStyle: 'italic',
                  fontSize: '12px',
                  color: 'var(--kahla-text-light)',
                  lineHeight: 1.3,
                }}>
                  {subtitle}
                </span>
              </span>
              <span
                aria-hidden="true"
                style={{
                  fontSize: '18px', color: 'var(--kahla-text-light)',
                  fontFamily: 'Playfair Display, serif',
                  lineHeight: 1, flexShrink: 0,
                }}
              >›</span>
            </button>
          );

          // Tiny inline SVGs — refined, no emoji, no FontAwesome.
          const PdfIcon = () => (
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 2.5h8l4 4V19.5a0 0 0 0 1 0 0H5a0 0 0 0 1 0 0V2.5z" />
              <path d="M13 2.5V6.5h4" />
              <line x1="7.5" y1="11" x2="14.5" y2="11" />
              <line x1="7.5" y1="14" x2="14.5" y2="14" />
              <line x1="7.5" y1="17" x2="11" y2="17" />
            </svg>
          );
          const ZipIcon = () => (
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3.5" y="3.5" width="15" height="15" rx="0.5" />
              <line x1="3.5" y1="8" x2="18.5" y2="8" />
              <line x1="11" y1="3.5" x2="11" y2="8" />
              <rect x="9.5" y="11" width="3" height="4" />
            </svg>
          );

          return (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <ExportAction
                  variant="primary"
                  label={t.export_pdf}
                  subtitle={t.export_pdf_subtitle}
                  onClick={() => setShowExportModal(true)}
                  disabled={pdfDisabled}
                  loading={exporting}
                  loadingLabel={t.exporting}
                  testId="export-pdf-btn"
                  IconSvg={PdfIcon}
                />
                <ExportAction
                  variant="secondary"
                  label={t.backup_zip}
                  subtitle={t.backup_zip_subtitle}
                  onClick={onExportZip}
                  disabled={zipDisabled}
                  loading={exportingZip}
                  loadingLabel={t.generating}
                  testId="export-zip-btn"
                  IconSvg={ZipIcon}
                />
              </div>

              {archive?.active && (archive?.pdf || archive?.zip) && (
                <div
                  data-testid="export-quota-row"
                  style={{
                    marginTop: '14px',
                    paddingTop: '12px',
                    borderTop: '1px solid var(--kahla-border)',
                    display: 'flex', flexDirection: 'column', gap: '4px',
                    fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                    fontStyle: 'italic',
                    fontSize: '12px',
                    lineHeight: 1.5,
                  }}
                >
                  {archive.pdf && (
                    <span
                      data-testid="export-pdf-quota-hint"
                      style={{ color: archive.pdf.remaining === 0 ? '#8a6a4a' : 'var(--kahla-text-light)' }}
                    >
                      {(t.pdf_quota_remaining || '{remaining} of {limit} PDF exports remaining')
                        .replace('{remaining}', String(archive.pdf.remaining))
                        .replace('{limit}', String(archive.pdf.limit))}
                    </span>
                  )}
                  {archive.zip && (
                    <span
                      data-testid="export-zip-quota-hint"
                      style={{ color: archive.zip.remaining === 0 ? '#8a6a4a' : 'var(--kahla-text-light)' }}
                    >
                      {(t.zip_quota_remaining || '{remaining} of {limit} backups remaining')
                        .replace('{remaining}', String(archive.zip.remaining))
                        .replace('{limit}', String(archive.zip.limit))}
                    </span>
                  )}
                </div>
              )}
            </>
          );
        })()}
      </div>

      <div className="settings-panel">
        <p className="settings-panel-title">{t.actions}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {board.current_status === 'ARCHIVED' ? (
            <button
              className="btn-secondary"
              style={{ width: '100%' }}
              onClick={handleRestoreBoard}
              data-testid="restore-board-btn"
            >
              {t.restore_board || 'Restore Board'}
            </button>
          ) : board.current_status !== 'EXPIRED' && (
            <button
              className="btn-secondary"
              style={{ width: '100%' }}
              onClick={handleArchiveBoard}
              data-testid="archive-board-btn"
            >
              {t.archive_board}
            </button>
          )}
          <button
            className="btn-secondary"
            style={{ width: '100%', color: '#C75050' }}
            onClick={() => setShowDeleteBoardModal(true)}
            data-testid="delete-board-btn"
          >
            {t.delete_board}
          </button>
        </div>
      </div>

      {/* Selected Image Details */}
      {selectedImage && (
        <div className="settings-panel" style={{ marginTop: '24px' }}>
          <p className="settings-panel-title">{t.selected_image}</p>
          <div className="form-group">
            <label>{t.section_label}</label>
            <select
              value={selectedImage.section_label || ''}
              onChange={(e) => {
                handleUpdateImage(selectedImage.id, { section_label: e.target.value || null });
                setSelectedImage({ ...selectedImage, section_label: e.target.value });
              }}
            >
              <option value="">{t.none_option}</option>
              {sectionLabels.map((label) => (
                <option key={label} value={label}>{label}</option>
              ))}
            </select>
            <button
              onClick={() => setShowLabelsModal(true)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '11px', color: 'var(--kahla-text-light)',
                padding: '4px 0', marginTop: '4px', textDecoration: 'underline',
              }}
              data-testid="manage-labels-btn"
            >
              {t.manage_labels}
            </button>
          </div>
          {selectedImage.library_image_id && (
            <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)', marginBottom: '12px' }}>
              📚 {t.from_library_shared}
            </p>
          )}
          <button
            className="btn-secondary"
            style={{
              width: '100%',
              marginBottom: '8px',
              ...(board.cover_image_id === selectedImage.id ? { background: 'var(--kahla-cream)', borderColor: 'var(--kahla-taupe)' } : {}),
            }}
            data-testid="set-as-cover-btn"
            onClick={async () => {
              const isCover = board.cover_image_id === selectedImage.id;
              try {
                await boardsAPI.update(board.id, { cover_image_id: isCover ? '' : selectedImage.id });
                setBoard((prev) => ({ ...prev, cover_image_id: isCover ? null : selectedImage.id }));
                showToast(isCover ? t.cover_removed : t.cover_set);
              } catch (err) {
                showToast(err.response?.data?.detail || 'Failed to update cover');
              }
            }}
          >
            {board.cover_image_id === selectedImage.id ? `✓ ${t.cover_image}` : t.set_as_cover}
          </button>
          <button
            className="btn-secondary"
            style={{ width: '100%', color: '#C75050' }}
            onClick={() => handleDeleteImage(selectedImage.id)}
          >
            {t.remove_from_board}
          </button>
        </div>
      )}
    </div>
  );
};

export default BoardSettingsPanel;
